import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { getMediaUrl } from '@/utils/media'
import { supportedLanguages, defaultLanguage, type SupportedLanguage } from '@/i18n'
import {
  findRouteKeyFromPath,
  getLocalizedPath,
} from '@/i18n/routes'

interface BreadcrumbItem {
  name: string
  path: string
}

interface SEOProps {
  title: string
  description: string
  image?: string
  type?: 'website' | 'article'
  noIndex?: boolean
  breadcrumbs?: BreadcrumbItem[]
  jsonLd?: object
}

const BASE_URL = 'https://www.chemicalnewtech.com'
const DEFAULT_IMAGE = getMediaUrl('logos/chemical-newtech-logo.png')
// Marker attribute used to identify head elements managed by this SEO
// component. On every render we remove all elements with this attribute
// before adding the new ones, avoiding duplicates after navigation.
// Also avoids conflicts with React 19's native head-metadata hoisting:
// since we mutate the DOM imperatively, React never sees our tags in its
// virtual DOM and never tries to hoist or hydrate them.
const SEO_MARKER = 'data-cnt-seo'

const LOCALE_MAP: Record<SupportedLanguage, string> = {
  en: 'en_US',
  it: 'it_IT',
  es: 'es_ES',
  de: 'de_DE',
  fr: 'fr_FR',
}

function parsePathInfo(pathname: string): {
  lang: SupportedLanguage
  pathSegment: string
} {
  const segments = pathname.split('/').filter(Boolean)
  const maybeLang = segments[0] as SupportedLanguage | undefined
  const lang =
    maybeLang && (supportedLanguages as readonly string[]).includes(maybeLang)
      ? (maybeLang as SupportedLanguage)
      : defaultLanguage
  const pathSegment = segments.slice(1).join('/')
  return { lang, pathSegment }
}

function clearManagedTags() {
  document
    .querySelectorAll(`[${SEO_MARKER}]`)
    .forEach((el) => el.parentNode?.removeChild(el))
}

function addMeta(
  attrName: 'name' | 'property',
  key: string,
  content: string
) {
  const el = document.createElement('meta')
  el.setAttribute(attrName, key)
  el.setAttribute('content', content)
  el.setAttribute(SEO_MARKER, '')
  document.head.appendChild(el)
}

function addLink(rel: string, href: string, extra?: Record<string, string>) {
  const el = document.createElement('link')
  el.setAttribute('rel', rel)
  el.setAttribute('href', href)
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      el.setAttribute(k, v)
    }
  }
  el.setAttribute(SEO_MARKER, '')
  document.head.appendChild(el)
}

function addJsonLd(payload: object) {
  const el = document.createElement('script')
  el.setAttribute('type', 'application/ld+json')
  el.textContent = JSON.stringify(payload)
  el.setAttribute(SEO_MARKER, '')
  document.head.appendChild(el)
}

export function SEO({
  title,
  description,
  image = DEFAULT_IMAGE,
  type = 'website',
  noIndex = false,
  breadcrumbs,
  jsonLd,
}: SEOProps) {
  const location = useLocation()
  const fullTitle = `${title} — Chemical Newtech`
  const url = `${BASE_URL}${location.pathname}`
  const imageUrl = image.startsWith('http') ? image : `${BASE_URL}${image}`

  const { lang: currentLang, pathSegment } = parsePathInfo(location.pathname)
  const routeKey = findRouteKeyFromPath(pathSegment, currentLang)

  useEffect(() => {
    // Clean slate: remove every previously-managed tag before adding new ones.
    clearManagedTags()

    // <title> doesn't get the marker (we just set it via document.title),
    // but we DO replace the existing one if it's also marked from a prior render.
    document.title = fullTitle
    document.documentElement.lang = currentLang

    addMeta('name', 'description', description)
    addMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow')

    // Open Graph
    addMeta('property', 'og:type', type)
    addMeta('property', 'og:title', fullTitle)
    addMeta('property', 'og:description', description)
    addMeta('property', 'og:image', imageUrl)
    addMeta('property', 'og:url', url)
    addMeta('property', 'og:site_name', 'Chemical Newtech')
    addMeta('property', 'og:locale', LOCALE_MAP[currentLang])
    for (const lang of supportedLanguages) {
      if (lang !== currentLang) {
        addMeta('property', 'og:locale:alternate', LOCALE_MAP[lang])
      }
    }

    // Twitter Card
    addMeta('name', 'twitter:card', 'summary_large_image')
    addMeta('name', 'twitter:title', fullTitle)
    addMeta('name', 'twitter:description', description)
    addMeta('name', 'twitter:image', imageUrl)

    // Canonical
    addLink('canonical', url)

    // hreflang alternates (only when we have a known route key)
    if (routeKey) {
      for (const lang of supportedLanguages) {
        addLink('alternate', `${BASE_URL}${getLocalizedPath(routeKey, lang)}`, {
          hreflang: lang,
        })
      }
      addLink(
        'alternate',
        `${BASE_URL}${getLocalizedPath(routeKey, defaultLanguage)}`,
        { hreflang: 'x-default' }
      )
    }

    // JSON-LD
    if (jsonLd) addJsonLd(jsonLd)
    if (breadcrumbs && breadcrumbs.length > 0) {
      addJsonLd({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: `${BASE_URL}${b.path}`,
        })),
      })
    }
  }, [
    fullTitle,
    description,
    imageUrl,
    url,
    type,
    noIndex,
    currentLang,
    routeKey,
    breadcrumbs,
    jsonLd,
  ])

  return null
}
